<script setup lang="ts">
const props = withDefaults(
	defineProps<{
		data: any[];
		levels: any;
		isLoading: any;
		isSaving: any;
		isDisabled?: boolean;
		canViewAllowances?: boolean;
		displayCaret?: boolean;
		displayAllowance?: boolean;
		search?: {
			placeholder: string;
			value: string;
			addTimeout: boolean;
		} | null;
		checkbox?: {
			label: string;
			checkedCount?: boolean;
			show?: boolean;
		} | null;
		isNewDocument?: boolean;
	}>(),
	{
		search: null,
		checkbox: null,
		displayCaret: true,
		displayAllowance: true,
		isNewDocument: false,
	},
);
const emit = defineEmits([
	"editDocument",
	"update-search-value",
	"select-all-checkbox",
	"check-el",
	"check-parent",
	"check-allowance",
	"select-all",
	"reset",
]);

const initData = ref(JSON.parse(JSON.stringify(props.data)));
const activeMenu = ref<string | null>("");
const isAllCheckboxSelected = ref(false);
const isLoadSelectAll = ref(false);

const ifTopLevelAnyCheckedLocations = computed(() => {
	if (props.levels[0] === "locations" && !props.isLoading) {
		const mapped = Array.isArray(initData.value)
			? initData.value.map((el) => el[props.levels[0]])
			: [];

		const flatFirst = mapped.flat().filter((inEl) => inEl.checked);
		const flatSecond = mapped.flat().filter((inEl) => !inEl.checked);

		return {
			all: flatFirst.length > 0,
			none: flatSecond.length > 0,
		};
	}
	return { all: false, none: false };
});

const ifTopLevelAnyCheckedSubcategories = computed(() => {
	if (props.levels[0] === "subcategories" && !props.isLoading) {
		const mapped = Array.isArray(initData.value)
			? initData.value.map((el) => el[props.levels[0]])
			: [];

		const flatFirst = mapped.flat().filter((inEl) => inEl.checked);
		const flatSecond = mapped.flat().filter((inEl) => !inEl.checked);

		return {
			all: flatFirst.length > 0,
			none: flatSecond.length > 0,
		};
	}
	return { all: false, none: false };
});

const resultData = computed(() => initData.value);

watch(
	() => props.data,
	() => {
		initData.value = JSON.parse(JSON.stringify(props.data));
		if (initData.value.length === 1) activeMenu.value = initData.value[0].id;
	},
);

watch(
	ifTopLevelAnyCheckedLocations,
	(val) => {
		if (val.all && val.none) {
			isAllCheckboxSelected.value = false;
		} else if (!val.none && val.all) {
			isAllCheckboxSelected.value = true;
		}
	},
	{ deep: true },
);

watch(
	ifTopLevelAnyCheckedSubcategories,
	(val) => {
		if (val.all && val.none) {
			isAllCheckboxSelected.value = false;
		} else if (!val.none && val.all) {
			isAllCheckboxSelected.value = true;
		}
	},
	{ deep: true },
);

// Methods
const changeAllSelected = async () => {
	isAllCheckboxSelected.value = !isAllCheckboxSelected.value;
	emit("select-all", isAllCheckboxSelected.value);
	isLoadSelectAll.value = true;

	await nextTick();

	const allTopInputs = Array.from(
		document.querySelectorAll(".top-check-input"),
	);

	if (isAllCheckboxSelected.value) {
		// @ts-ignore
		for (const input of allTopInputs) !input.checked && input.click();
	} else {
		// @ts-ignore
		for (const input of allTopInputs) input.checked && input.click();
	}

	isLoadSelectAll.value = false;
};

const hasAnyAllowanceSetting = (levelsRows: any[]) => {
	return levelsRows.some((row) => row.hasComplianceAllowance);
};

const rootStatus = (obj: any[]) => {
	const checked = obj.filter((el) => el.checked === true);
	return checked.length === obj.length && obj.length !== 0;
};

const checkNumber = (obj: any[]) => {
	const checked = obj.filter((el) => el.checked === true);
	return checked.length;
};

const ifOnlyAnyChecked = (arr: any[]) => {
	return arr.some((el) => el.checked) && arr.some((el) => el.checked === false);
};

const checkRoot = (obj: any) => {
	if (rootStatus(obj)) {
		for (const key of Object.keys(obj)) {
			obj[key].checked = false;
		}
	} else {
		for (const key of Object.keys(obj)) {
			obj[key].checked = true;
		}
	}
	emit("check-parent", obj);
};

const toggle = (id: string) => {
	activeMenu.value = activeMenu.value === id ? "" : id;
};

const setCheck = (
	id: string,
	centres: any[],
	checked: boolean,
	property: string,
) => {
	const who = centres.find((el) => el.id === id);

	if (who) {
		who[property] = !checked;
	}
	if (property === "hasComplianceAllowance") {
		emit("check-allowance", { id, checked });
	}
};

const countSelected = computed(() => {
	let count: number = 0;
	resultData.value.map((obj: any) => {
		// @ts-ignore
		obj[props.levels[0]].filter((t: any) => {
			if (t.checked === true) {
				count++;
			}
		});
	});
	return count;
});
const onSaveForm = () => {
	const count = resultData.value.map((obj: any) => {
		// @ts-ignore
		return obj[props.levels[0]].filter((t: any) => t.checked === true).length;
	});

	// Replace reduce with a for loop
	let totalChecked = 0;
	for (const element of count) {
		totalChecked += element;
	}

	const result = {
		entity: props.levels,
		data: resultData.value,
	};

	emit("editDocument", result, totalChecked);
};

const reset = () => {
	if (props.levels[0] === "locations") {
		emit("reset");
	} else {
		initData.value = JSON.parse(JSON.stringify(props.data));
	}
};

const onClickInputRow = (
	id: string,
	levelGroup: any[],
	itemRow: any,
	checked: boolean,
) => {
	if (!isDisabledInput(itemRow)) {
		setCheck(id, levelGroup, checked, "checked");
		emit("check-el", { id, itemRow, checked });
	}
};

const isDisabledInput = (itemRow: any) => {
	if (props.levels[0] === "locations") {
		return Boolean(props.isDisabled || itemRow.archived);
	}
	return Boolean(props.isDisabled);
};

function updateSearchValue(val: string): void {
	emit("update-search-value", val);
}
onMounted(() => {
	if (props.levels[0] === "locations") {
		isAllCheckboxSelected.value =
			ifTopLevelAnyCheckedLocations.value?.all &&
			ifTopLevelAnyCheckedLocations.value?.none
				? false
				: ifTopLevelAnyCheckedLocations.value?.all
					? true
					: false;
	}
	if (props.levels[0] === "subcategories") {
		isAllCheckboxSelected.value =
			ifTopLevelAnyCheckedSubcategories.value?.all &&
			ifTopLevelAnyCheckedSubcategories.value?.none
				? false
				: ifTopLevelAnyCheckedSubcategories.value?.all
					? true
					: false;
	}
});
</script>

<template>
	<div
		class="edit-documents-details-wrap"
		:class="{ scrollable: isLoading }"
	>
		<template v-if="isLoading">
			<div
				v-if="isLoading"
				class="loading-wrapper flex-center"
			>
				<img src="@/assets/image/loader/loader.svg" />
			</div>
		</template>
		<template v-if="!isLoading">
			<div
				class="header"
				:style="!isNewDocument && !isDisabled ? 'min-height: 32px' : ''"
			>
				<div
					v-if="
						props?.checkbox?.show &&
						(levels[0] === 'locations' || levels[0] === 'subcategories') &&
						!isDisabled
					"
					class="pd-5"
					style="display: flex; align-items: center; gap: 5px"
				>
					<input
						id="edit-document-details-select-all"
						type="checkbox"
						:checked="isAllCheckboxSelected"
						@change="changeAllSelected"
					/>
					<label
						for="edit-document-details-select-all"
						style="cursor: pointer; margin: 0"
					>
						{{ props.checkbox.label }}
						{{
							props?.checkbox?.checkedCount ? `(${countSelected} checked)` : ""
						}}
					</label>
				</div>
				<div
					v-if="!isNewDocument && !isDisabled"
					class="refresh-wrap"
				>
					<i-fa4-refresh
						title="Undo"
						@click="reset()"
					/>
				</div>
			</div>

			<div
				v-if="
					props.search &&
					props.search?.placeholder &&
					props.search?.placeholder.length > 0
				"
			>
				<search-input
					:placeholder="props.search.placeholder"
					:search-value="props.search.value"
					:addTimeout="false"
					style="margin-bottom: 10px"
					@update:search-value="updateSearchValue"
				></search-input>
			</div>
			<label
				v-if="levels[0] === 'locations' && !isDisabled && !props?.checkbox"
				class="ctrl ctrl-checkbox"
				style="margin-left: 5px; margin-bottom: 5px"
				@click.prevent="changeAllSelected"
			>
				Select all
				<input
					class="ctrl-checkbox"
					type="checkbox"
					:checked="isAllCheckboxSelected"
				/>
				<div
					class="ctrl__status"
					:class="{
						'status-halfstate':
							ifTopLevelAnyCheckedLocations.all &&
							ifTopLevelAnyCheckedLocations.none,
					}"
				></div>
				<img
					v-if="isLoadSelectAll"
					src="@/assets/image/loader/loader.svg"
					style="height: 20px; margin-left: 10px"
				/>
			</label>
			<ul style="overflow: auto">
				<li
					v-for="el in initData"
					:key="el.id"
				>
					<div
						v-if="el[levels].length != 0"
						class="row"
					>
						<label
							class="ctrl ctrl-checkbox"
							:class="{ disabled: isDisabled }"
							:title="el.name"
							@click.prevent="isDisabled ? null : checkRoot(el[levels])"
						>
							{{ el.name }}
							<input
								class="top-check-input ctrl-checkbox"
								type="checkbox"
								:checked="rootStatus(el[levels])"
							/>
							<div
								class="ctrl__status"
								:class="{ 'status-halfstate': ifOnlyAnyChecked(el[levels]) }"
							></div>
						</label>
						<span class="circle">{{ checkNumber(el[levels]) }}</span>
						<span
							v-if="displayCaret"
							class="fa drop-arrow"
							data-cell="arrow-button"
							:class="{
								'fa-angle-down': el.id != activeMenu,
								'fa-angle-up': el.id == activeMenu,
							}"
							@click.prevent="toggle(el.id)"
						></span>
					</div>
					<transition name="fade">
						<ul
							v-if="displayCaret ? activeMenu == el.id : true"
							class="target"
						>
							<li
								v-if="displayAllowance && levels[0] === 'locations'"
								class="li-label"
							>
								<span>Ward</span>
								<span
									v-if="hasAnyAllowanceSetting(el[levels]) && canViewAllowances"
								>
									Has Allowance?
								</span>
							</li>
							<li
								v-for="itemRow in el[levels]"
								:key="itemRow.id"
							>
								<div class="row">
									<label
										class="ctrl ctrl-checkbox"
										:class="{ disabled: isDisabledInput(itemRow) }"
										:title="itemRow.name"
										@click.prevent="
											onClickInputRow(
												itemRow.id,
												el[levels],
												itemRow,
												itemRow.checked,
											)
										"
									>
										{{ itemRow.name }}
										<input
											class="ctrl-checkbox"
											type="checkbox"
											:checked="itemRow.checked"
											:disabled="isDisabledInput(itemRow)"
										/>
										<div class="ctrl__status"></div>
									</label>
									<span
										v-if="
											itemRow.hasOwnProperty('hasComplianceAllowance') &&
											canViewAllowances
										"
										class="ctrl-switch"
										@click.prevent="
											() => {
												if (isDisabled) return;
												setCheck(
													itemRow.id,
													el[levels],
													itemRow.hasComplianceAllowance,
													'hasComplianceAllowance',
												);
											}
										"
									>
										<switch-component
											v-if="itemRow?.checked"
											theme="bulma"
											color="blue"
											:disabled="isDisabled"
											:modelValue="itemRow?.hasComplianceAllowance ?? false"
											@update:model-value="
												itemRow.hasComplianceAllowance =
													!itemRow.hasComplianceAllowance
											"
										/>
									</span>
								</div>
							</li>
						</ul>
					</transition>
				</li>
			</ul>
			<div class="columns spacer">
				<button
					v-if="!isDisabled && !isNewDocument"
					class="button small is-fullwidth"
					:class="{ 'is-loading': isSaving }"
					:disabled="isSaving || countSelected < 1"
					@click="onSaveForm"
				>
					SAVE
				</button>
			</div>
		</template>
	</div>
</template>

<style scoped lang="scss">
$color-white: #fff;
$color-light-grey: #e3ebed;
$color-grey: #b6b6b6;
$color-dark-blue: #405168;
$color-dark-grey: #7b7b7b;
$color-primary: #2669b0;
$color-secondary: #7c9ec9;
$color-label: #bbb;
$color-border-label: #eee;

.edit-documents-details-wrap {
	height: 590px;
	overflow-y: hidden;
	overflow-x: hidden;
	display: flex;
	flex-direction: column;
	gap: 0;
	.columns {
		margin-top: 20px;
	}

	.header {
		display: flex;
		justify-content: space-between;
		padding-right: 10px;
		align-items: center;

		.refresh-wrap {
			cursor: pointer;
			margin-left: auto;
		}
	}

	.spacer {
		display: flex;
		justify-content: space-between;

		.button {
			height: 33px;
			width: 100px;
		}
	}

	.p-title {
		opacity: 0.4;
		color: $color-dark-blue;
		text-align: center;
		padding: 10px;

		span {
			font-size: 11px;
			font-weight: bold;
			line-height: 13px;
		}
	}

	.circle {
		border-radius: 50%;
		background: $color-light-grey;
		width: 24px;
		height: 24px;
		line-height: 24px;
		color: $color-dark-blue;
		text-align: center;
		font-weight: 500;
	}

	> ul {
		padding-bottom: 10px;
	}

	label.disabled {
		color: $color-grey;
		cursor: default;
	}
}

div.row {
	display: flex;
	flex: 1 100%;
	flex-wrap: wrap;
	align-items: center;
	padding: 0 10px 5px 5px;

	label {
		flex: 2;
		font-size: 14px;
		color: $color-dark-blue;
		font-weight: 500;
	}
}

.ctrl {
	display: block;
	position: relative;
	padding-left: 20px;
	cursor: pointer;
	font-size: 18px;

	input {
		position: absolute;
		z-index: -1;
		opacity: 0;
	}
}

.ctrl__status {
	position: absolute;
	top: 5px;
	left: 0;
	height: 12px;
	width: 12px;
	background: $color-white;
	border: 1px solid $color-secondary;
	border-radius: 2px;

	.ctrl input:checked ~ & {
		background: $color-primary;
	}

	.ctrl input:disabled ~ & {
		background: $color-light-grey;
		opacity: 0.6;
		pointer-events: none;
	}

	&::after {
		content: "";
		position: absolute;
		display: none;
		.ctrl-checkbox & {
			left: 3px;
			top: 1px;
			width: 4px;
			height: 8px;
			border: solid $color-white;
			border-width: 0 2px 2px 0;
			transform: rotate(45deg);
		}
		.ctrl input:checked ~ & {
			display: block;
		}

		.ctrl-checkbox input:disabled ~ & {
			border-color: $color-dark-grey;
		}
	}
}

.ctrl-switch {
	display: inline-block;
	padding-left: 10px;
}

.status-halfstate {
	background: linear-gradient(
		to right bottom,
		$color-white 50%,
		$color-primary 50%
	);
}

.fade-enter-to,
.fade-leave {
	max-height: 500px;
}

.fade-enter,
.fade-leave-to {
	max-height: 0;
}

.target {
	transition: all 0.5s;
	overflow: hidden;

	li {
		margin: 0 10px;
		.row > label {
			width: 100%;
			overflow: hidden;
			text-wrap: nowrap;
			text-overflow: ellipsis;
		}

		&:first-child {
			border-top: 1px solid $color-light-grey;
			padding-top: 5px;
		}
	}
}

li.li-label {
	display: flex;
	justify-content: space-between;
	color: $color-label;
	border-bottom: 1px solid $color-border-label;
	padding-bottom: 3px;
	margin-bottom: 5px;
}

.loading-wrapper {
	text-align: center;
}

.button-wrapper {
	margin: 10px 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.vue-switcher div {
	height: 5px;
	width: 30px;
	border-radius: 4px;

	&::after {
		margin-left: -14px;
		height: 12px;
		width: 12px;
	}
}
</style>
